/**
 * „Tydzień wzorcowy" while it is being edited, and its one save.
 *
 * The card edits a draft of the whole week (`model/weekPattern`) and „Zapisz" sends it whole —
 * `PUT /trainer-availability/weekly` replaces the grid. Until the trainer touches a day the card
 * shows the server's week as it stands, so a refresh from elsewhere lands on it; the first edit
 * forks a draft, and a save (or a discard) returns the card to the server's reading.
 *
 * ── Unsaved hours are guarded ────────────────────────────────────────────────────────────────
 * An edited week that has not been saved is work the trainer did: leaving the tab or the screen
 * with one standing asks first — „Porzucić zmiany w tygodniu?" — through the router's blocker, the
 * house pattern (`pages/reports`'s editor). Choosing another tab is leaving: the draft lives in
 * this tab.
 *
 * ── A save keeps the week it sent on screen ───────────────────────────────────────────────────
 * The PUT answers with nothing, so on success the cached read takes the week that was sent
 * (`weekSaved`) before the draft is let go: no frame shows the old hours between the answer and
 * the read after it. While the PUT is out the card is frozen — an edit made then would be neither
 * in the week sent nor kept after it.
 *
 * ── A week changed elsewhere is not overwritten silently ──────────────────────────────────────
 * The draft remembers the server's week it forked from. If a later read brings a different one
 * while the draft is unsaved (the phone, another tab), „Zapisz" holds and the card names the other
 * week in one line (`weekLine`) and offers both ways out by their consequence: „Wczytaj tamten
 * tydzień" drops the draft (the card asks first), „Zapisz moją wersję" keeps it to be sent over the
 * other week. Each says what happened.
 */

import { useBlocker } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePutTrainerAvailabilityWeekly } from '~/shared/api/generated/endpoints/trainer-availability/trainer-availability';
import type { TrainWithMeModulesBookingDtosResponsesWeeklyWindowResponse as WeeklyWindowResponse } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';
import { readBookingAgain } from '~/features/credit-ledger';

import { weekSaved } from '../api/bookingQueries';
import { scheduleRefusal } from '../model/scheduleRefusal';
import { weekLine } from '../model/scheduleWords';
import {
  MAX_WEEKLY_WINDOWS,
  patternChanged,
  patternVerdict,
  weekPatternOf,
  weeklyRequest,
  type PatternVerdict,
  type WeekPattern,
} from '../model/weekPattern';
import type { ScheduleRead } from './useScheduleRead';

export interface WeekDraft {
  readonly pattern: WeekPattern;
  readonly verdict: PatternVerdict;
  readonly dirty: boolean;
  readonly saving: boolean;
  /** Why „Zapisz" will not send, or `null` when it will. */
  readonly saveBlocked: string | null;
  /** The last refused save, act first. */
  readonly refusal: string | null;
  readonly edit: (change: (pattern: WeekPattern) => WeekPattern) => void;
  readonly save: () => void;
  /** The leave question, while a navigation is held for it. */
  readonly leavePrompt: ConfirmPrompt | null;
  readonly leave: () => void;
  readonly stay: () => void;
  /** The server's week changed under an unsaved draft. */
  readonly drifted: boolean;
  /** That other week in one line, while drifted. */
  readonly elsewhere: string | null;
  /** Drop the draft for the server's current week. */
  readonly reload: () => void;
  /** Keep the draft, to be sent over the server's current week. */
  readonly overwrite: () => void;
}

export function useWeekDraft(read: ScheduleRead, announce: (message: string) => void): WeekDraft {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [edited, setEdited] = useState<WeekPattern | null>(null);
  // The server's week the draft was forked from.
  const [base, setBase] = useState<readonly WeeklyWindowResponse[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const weekly = read.availability?.weekly ?? [];
  const pattern = edited ?? weekPatternOf(weekly);
  const verdict = patternVerdict(pattern);
  const dirty = edited !== null && patternChanged(edited, weekly);
  const drifted = dirty && base !== null && patternChanged(weekPatternOf(base), weekly);

  const put = usePutTrainerAvailabilityWeekly({
    mutation: {
      onSuccess: (_answer, { data }) => {
        weekSaved(queryClient, data?.windows ?? []);
        setEdited(null);
        setBase(null);
        setRefusal(null);
        announce(t('web:booking.schedule.saved'));
        void readBookingAgain(queryClient);
      },
      onError: (error) => {
        const refused = scheduleRefusal(
          'saveWeek',
          error instanceof ApiError ? { status: error.status, code: error.code } : null,
        );
        setRefusal(`${t(refused.actKey)} ${t(refused.reasonKey, refused.params)}`);
      },
    },
  });

  const saveBlocked = !dirty
    ? t('web:booking.schedule.noChanges')
    : drifted
      ? t('web:booking.schedule.drift.blocked')
      : verdict.tooMany
        ? t('web:booking.schedule.tooMany', { max: MAX_WEEKLY_WINDOWS })
        : !verdict.canSave
          ? t('web:booking.schedule.invalid')
          : null;

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (current.pathname !== next.pathname) return true;
      const nextTab = (next.search as { readonly tab?: string }).tab;
      return nextTab !== 'grafik';
    },
    enableBeforeUnload: true,
    disabled: !dirty,
    withResolver: true,
  });

  return {
    pattern,
    verdict,
    dirty,
    saving: put.isPending,
    saveBlocked,
    refusal,
    edit: (change) => {
      if (put.isPending) return;
      setRefusal(null);
      if (edited === null) setBase(weekly);
      setEdited(change(pattern));
    },
    save: () => {
      if (saveBlocked !== null || put.isPending) return;
      put.mutate({ data: weeklyRequest(pattern) });
    },
    leavePrompt:
      blocker.status === 'blocked'
        ? {
            title: t('web:booking.schedule.leaveTitle'),
            description: t('web:booking.schedule.leaveBody'),
            confirmLabel: t('web:booking.schedule.leaveConfirm'),
            cancelLabel: t('web:booking.schedule.leaveCancel'),
            destructiveConfirm: true,
          }
        : null,
    leave: () => {
      if (blocker.status === 'blocked') blocker.proceed();
    },
    stay: () => {
      if (blocker.status === 'blocked') blocker.reset();
    },
    drifted,
    elsewhere: drifted ? weekLine(weekPatternOf(weekly), t, i18n.language) : null,
    reload: () => {
      setEdited(null);
      setBase(null);
      setRefusal(null);
      announce(t('web:booking.schedule.drift.reloaded'));
    },
    overwrite: () => {
      setBase(weekly);
      announce(t('web:booking.schedule.drift.overwriting'));
    },
  };
}
